import { supertypeClass, Bindable, Remoteable, Persistable, Supertype } from '@haventech/amorphic';
import { Product, productTypes } from '../../../../../common/js/static/Product';
import { Utils } from '../../../../../common/js/Utils';
import { PaymentTerms, WholeLifePaymentTerms, PaymentOptionWholeLife } from '../../HtmlPdfTypes';
import { HtmlPdfUtils } from '../../HtmlPdfUtils';
import { IllustrationRequest } from '../../IllustrationRequest';

export class CssStyle {
  HtmlPdfSummaryTableStyle: string = '';
}
class SummaryTable {
  HtmlPdfSummaryTable: string = '';
  HtmlPdfSummaryTableWaiverOfPremiumRow: string = '';
  HtmlPdfSummaryTableWaiverAdditionalFrequencies: string = '';
  HtmlPdfSummaryTableAdditionalFrequencies: string = '';
  HtmlPdfSummaryTableRiders: string = '';
  css: CssStyle = new CssStyle();
}

@supertypeClass({ toClient: true, toServer: true })
export class HtmlPdfSummaryTable extends Bindable(Remoteable(Persistable(Supertype))) {
  private templateFolder = './apps/coverpath-common/classes/wholelife/components/summarytable/templates/';

  async getHtmlContent_SummaryTable(illustrationRequest: IllustrationRequest, paymentOptions: PaymentTerms) {
    const st = await HtmlPdfUtils.getContent(this.templateFolder, new SummaryTable());
    const style = `${st.HtmlPdfSummaryTableStyle}`;
    const hasWaiver = illustrationRequest.waiverUnits > 0;
    const displayFaceAmount =
      illustrationRequest.newFaceAmount > 0 && illustrationRequest.faceAmount > illustrationRequest.newFaceAmount
        ? Utils.formatCurrency(illustrationRequest.newFaceAmount, 0, false)
        : Utils.formatCurrency(illustrationRequest.faceAmount, 0, false);

    let body: string = st.HtmlPdfSummaryTableAdditionalFrequencies;
    body = body
      .replace(/{{TableTitle}}/, 'Current Policy Year')
      .replace(/{{WaiverOfPremiumRow}}/, hasWaiver ? st.HtmlPdfSummaryTableWaiverAdditionalFrequencies : '')
      .replace(/{{PremiumRiders}}/, '')
      .replace(/{{WOPRiders}}/, '')
      .replace(
        // FaceAmount should come after WaiverOfPremiumRow because it uses the {{FaceAmount}} placeholder
        /{{FaceAmount}}/g,
        displayFaceAmount
      )
      .replace(/{{PolicyYear}}/g, (Utils.getActualAge(illustrationRequest.startDate) + 2).toString())
      .replace(
        /{{BasePremiumAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.annual ? paymentOptions.annual.basePremium : 0, 2), 2)
      )
      .replace(
        /{{BasePremiumMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly ? paymentOptions.monthly.basePremium : 0, 2), 2)
      )
      .replace(
        /{{WaiverAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.annual ? paymentOptions.annual.waiver : 0, 2), 2)
      )
      .replace(
        /{{WaiverMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly ? paymentOptions.monthly.waiver : 0, 2), 2)
      )
      .replace(
        /{{ModalPremiumAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.annual ? paymentOptions.annual.total : 0, 2), 2)
      )
      .replace(
        /{{ModalPremiumMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly ? paymentOptions.monthly.total : 0, 2), 2)
      )
      .replace(
        /{{ModalAnnualizedPremiumAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.annual ? paymentOptions.annual.total : 0, 2), 2)
      )
      .replace(
        /{{PremiumRiders}}/, // not needed for term, but remove it from the HTML
        ''
      )
      .replace(
        /{{WOPRiders}}/, // not needed for term, but remove it from the HTML
        ''
      )
      .replace(
        /{{BasePremiumSemiAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.semiannual.basePremium, 2), 2)
      )
      .replace(/{{BasePremiumQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.basePremium, 2), 2))
      .replace(/{{ModalPremiumSemiAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.semiannual.total, 2), 2))
      .replace(/{{ModalPremiumQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.total, 2), 2))
      .replace(
        /{{ModalAnnualizedPremiumSemiAnnual}}/,
        Utils.formatNumber(Utils.round(paymentOptions.semiannual.annualTotal, 2), 2)
      )
      .replace(
        /{{ModalAnnualizedPremiumQuarterly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.quarterly.annualTotal, 2), 2)
      )
      .replace(/{{WaiverSemiAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.semiannual.waiver, 2), 2))
      .replace(/{{WaiverQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.waiver, 2), 2))
      .replace(
        /{{ModalAnnualizedPremiumMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly ? paymentOptions.monthly.annualTotal : 0, 2), 2)
      );
    return HtmlPdfUtils.getHtmlContent(style, body);
  }

  async getHtmlContent_SummaryTable_WholeLife(
    illustrationRequest: IllustrationRequest,
    wholeLifePaymentOptions: WholeLifePaymentTerms,
    isSupplemental: boolean = false
  ) {
    const st = await HtmlPdfUtils.getContent(this.templateFolder, new SummaryTable());
    const style = `${st.HtmlPdfSummaryTableStyle}`;
    const hasWaiver = illustrationRequest.waiverUnits > 0;
    const displayFaceAmount = isSupplemental
      ? Utils.formatCurrency(illustrationRequest.newFaceAmount, 0, false)
      : Utils.formatCurrency(illustrationRequest.faceAmount, 0, false);

    const body: string = st.HtmlPdfSummaryTableAdditionalFrequencies.replace(
      /{{TableTitle}}/,
      `Premiums (Policy Year ${Utils.getActualAge(illustrationRequest.startDate) + 2})`
    )
      .replace(/{{WaiverOfPremiumRow}}/, hasWaiver ? st.HtmlPdfSummaryTableWaiverAdditionalFrequencies : '')
      .replace(
        /{{PremiumRiders}}/,
        getPremiumRiders(
          wholeLifePaymentOptions.monthly,
          wholeLifePaymentOptions.quarterly,
          wholeLifePaymentOptions.semiannual,
          wholeLifePaymentOptions.annual,
          illustrationRequest,
          isSupplemental
        )
      ) // i needed to use 'monthly' and 'annual' here because it wouldn't recongize the type because of how the object is constructed
      .replace(
        /{{WOPRiders}}/,
        hasWaiver
          ? getWaiverOfPremiumRiders(
              wholeLifePaymentOptions.monthly,
              wholeLifePaymentOptions.quarterly,
              wholeLifePaymentOptions.semiannual,
              wholeLifePaymentOptions.annual,
              illustrationRequest,
              isSupplemental
            )
          : ''
      )
      .replace(
        // FaceAmount should come after WaiverOfPremiumRow because it uses the {{FaceAmount}} placeholder
        /{{FaceAmount}}/g,
        displayFaceAmount
      )
      .replace(/{{PolicyYear}}/g, Utils.getActualAge(illustrationRequest.startDate) + 2)
      .replace(
        /{{BasePremiumAnnual}}/,
        Utils.formatNumber(
          Utils.round(wholeLifePaymentOptions.annual ? wholeLifePaymentOptions.annual.basePolicyPremium : 0, 2),
          2
        )
      )
      .replace(
        /{{BasePremiumMonthly}}/,
        Utils.formatNumber(
          Utils.round(wholeLifePaymentOptions.monthly ? wholeLifePaymentOptions.monthly.basePolicyPremium : 0, 2),
          2
        )
      )
      .replace(
        /{{WaiverAnnual}}/,
        Utils.formatNumber(
          Utils.round(wholeLifePaymentOptions.annual ? wholeLifePaymentOptions.annual.waiverPremium : 0, 2),
          2
        )
      )
      .replace(
        /{{WaiverMonthly}}/,
        Utils.formatNumber(
          Utils.round(wholeLifePaymentOptions.monthly ? wholeLifePaymentOptions.monthly.waiverPremium : 0, 2),
          2
        )
      )
      .replace(
        /{{ModalPremiumAnnual}}/,
        Utils.formatNumber(
          Utils.round(wholeLifePaymentOptions.annual ? wholeLifePaymentOptions.annual.totalInitialModalPremium : 0, 2),
          2
        )
      )
      .replace(
        /{{ModalPremiumMonthly}}/,
        Utils.formatNumber(
          Utils.round(
            wholeLifePaymentOptions.monthly ? wholeLifePaymentOptions.monthly.totalInitialModalPremium : 0,
            2
          ),
          2
        )
      )
      .replace(
        /{{ModalAnnualizedPremiumAnnual}}/,
        Utils.formatNumber(
          Utils.round(
            wholeLifePaymentOptions.annual ? wholeLifePaymentOptions.annual.totalInitialModalPremiumAnnualized : 0,
            2
          ),
          2
        )
      )
      .replace(
        /{{ModalAnnualizedPremiumMonthly}}/,
        Utils.formatNumber(
          Utils.round(
            wholeLifePaymentOptions.monthly ? wholeLifePaymentOptions.monthly.totalInitialModalPremiumAnnualized : 0,
            2
          ),
          2
        )
      )
      .replace(
        /{{BasePremiumSemiAnnual}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.semiannual.basePolicyPremium, 2), 2)
      )
      .replace(
        /{{BasePremiumQuarterly}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.quarterly.basePolicyPremium, 2), 2)
      )
      .replace(
        /{{ModalPremiumSemiAnnual}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.semiannual.totalInitialModalPremium, 2), 2)
      )
      .replace(
        /{{ModalPremiumQuarterly}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.quarterly.totalInitialModalPremium, 2), 2)
      )
      .replace(
        /{{ModalAnnualizedPremiumSemiAnnual}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.semiannual.totalInitialModalPremiumAnnualized, 2), 2)
      )
      .replace(
        /{{ModalAnnualizedPremiumQuarterly}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.quarterly.totalInitialModalPremiumAnnualized, 2), 2)
      )
      .replace(
        /{{WaiverSemiAnnual}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.semiannual.waiverPremium, 2), 2)
      )
      .replace(
        /{{WaiverQuarterly}}/,
        Utils.formatNumber(Utils.round(wholeLifePaymentOptions.quarterly.waiverPremium, 2), 2)
      );

    return body;

    //  loops through riders from request and if they exsists it adds them to the coverage information breakdown
    // applies a HtmlPdfNarrativeSummaryRider template for each rider present
    function getPremiumRiders(
      monthly: PaymentOptionWholeLife,
      quarterly: PaymentOptionWholeLife,
      semiannual: PaymentOptionWholeLife,
      annual: PaymentOptionWholeLife,
      illustrationRequest: IllustrationRequest,
      isSupplemental: boolean
    ) {
      let riderHtml = '';

      illustrationRequest.riders.forEach((rider) => {
        switch (rider.type) {
          case 'GIR':
            const amount =
              isSupplemental && illustrationRequest.girAmountToConvert > 0
                ? rider.amount - illustrationRequest.girAmountToConvert
                : rider.amount;
            riderHtml =
              riderHtml +
              st.HtmlPdfSummaryTableRiders.replace(/{{RiderTitle}}/, 'Guaranteed Insurability Rider')
                .replace(/{{FaceAmount}}/, Utils.formatCurrency(amount))
                .replace(/{{RiderAnnual}}/, Utils.formatCurrency(annual.GIRPremium))
                .replace(/{{RiderSemiAnnual}}/, Utils.formatCurrency(semiannual.GIRPremium))
                .replace(/{{RiderQuarterly}}/, Utils.formatCurrency(quarterly.GIRPremium))
                .replace(/{{RiderMonthly}}/, Utils.formatCurrency(monthly.GIRPremium));
            break;
          case 'ALIR':
            riderHtml =
              riderHtml +
              st.HtmlPdfSummaryTableRiders.replace(/{{RiderTitle}}/, 'Unscheduled ALIR (Year 1)')
                .replace(/{{FaceAmount}}/, 'N/A')
                .replace(/{{RiderAnnual}}/, Utils.formatCurrency(rider.amount))
                .replace(/{{RiderMonthly}}/, Utils.formatCurrency(rider.amount / 12));

            break;
          case 'RTR':
            riderHtml =
              riderHtml +
              st.HtmlPdfSummaryTableRiders.replace(/{{RiderTitle}}/, 'Renewable Term Rider')
                .replace(/{{FaceAmount}}/, Utils.formatCurrency(rider.amount))
                .replace(/{{RiderAnnual}}/, Utils.formatCurrency(annual.RTRPremium))
                .replace(/{{RiderSemiAnnual}}/, Utils.formatCurrency(semiannual.RTRPremium))
                .replace(/{{RiderQuarterly}}/, Utils.formatCurrency(quarterly.RTRPremium))
                .replace(/{{RiderMonthly}}/, Utils.formatCurrency(monthly.RTRPremium));

            break;
          default:
            break;
        }
      });
      return riderHtml;
    }

    //  loops through riders from request and if they exsists it adds them to the coverage information breakdown
    // applies a HtmlPdfNarrativeSummaryRider template for each rider present
    function getWaiverOfPremiumRiders(
      monthly: PaymentOptionWholeLife,
      quarterly: PaymentOptionWholeLife,
      semiannual: PaymentOptionWholeLife,
      annual: PaymentOptionWholeLife,
      illustrationRequest: IllustrationRequest,
      isSupplemental: boolean
    ) {
      let riderHtml = '';

      illustrationRequest.riders.forEach((rider) => {
        switch (rider.type) {
          case 'GIR':
            const girFaceAmount =
              isSupplemental && illustrationRequest.girAmountToConvert > 0
                ? rider.amount - illustrationRequest.girAmountToConvert
                : rider.amount;
            riderHtml =
              riderHtml +
              st.HtmlPdfSummaryTableRiders.replace(/{{RiderTitle}}/, 'Guaranteed	Insurability	Waiver')
                .replace(/{{FaceAmount}}/, Utils.formatCurrency(girFaceAmount))
                .replace(/{{RiderAnnual}}/, Utils.formatCurrency(annual.GIRWaiverPremium))
                .replace(/{{RiderSemiAnnual}}/, Utils.formatCurrency(semiannual.GIRWaiverPremium))
                .replace(/{{RiderQuarterly}}/, Utils.formatCurrency(quarterly.GIRWaiverPremium))
                .replace(/{{RiderMonthly}}/, Utils.formatCurrency(monthly.GIRWaiverPremium));
            break;
          case 'RTR':
            riderHtml =
              riderHtml +
              st.HtmlPdfSummaryTableRiders.replace(/{{RiderTitle}}/, 'Renewable Term Waiver')
                .replace(/{{FaceAmount}}/, Utils.formatCurrency(rider.amount))
                .replace(/{{RiderAnnual}}/, Utils.formatCurrency(annual.RTRWaiverPremium))
                .replace(/{{RiderSemiAnnual}}/, Utils.formatCurrency(semiannual.RTRWaiverPremium))
                .replace(/{{RiderQuarterly}}/, Utils.formatCurrency(quarterly.RTRWaiverPremium))
                .replace(/{{RiderMonthly}}/, Utils.formatCurrency(monthly.RTRWaiverPremium));

            break;
          default:
            break;
        }
      });
      return riderHtml;
    }
  }
}
